import axios, { type AxiosError, type AxiosInstance } from 'axios';

import type { AuthSession, SessionStorage } from './api-types.js';

export interface CreateApiOptions {
  baseUrl: string;
  storage: SessionStorage;
  onUnauthorized?(): void;
  /** Session renouvelée (tokens + profil à jour, rôle compris) : à répercuter dans l'état de l'app. */
  onSessionRefreshed?(s: AuthSession): void;
}

// Un 401 sur ces routes est un refus d'identifiants, pas une session expirée : ni refresh, ni onUnauthorized
// (sinon le login échoué déclenche la redirection /login = rechargement complet de la page).
// Routes publiques (/public/verify, /public/invitations…) : leur 401 = lien invalide, affiché par la page (8.4.5).
const AUTH_ENDPOINT = /^\/?(auth\/(login|refresh)(\?|$)|public\/)/;

export const createApi = (opts: CreateApiOptions): AxiosInstance => {
  const client = axios.create({ baseURL: `${opts.baseUrl}/api/v1`, timeout: 30_000 });

  client.interceptors.request.use((config) => {
    const s = opts.storage.get();
    if (s?.accessToken) config.headers.Authorization = `Bearer ${s.accessToken}`;
    return config;
  });

  let refreshing: Promise<string> | null = null;

  client.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const original = error.config!;
      if (error.response?.status !== 401 || (original as { _retry?: boolean })._retry) throw error;
      if (AUTH_ENDPOINT.test(original.url ?? '')) throw error;
      (original as { _retry?: boolean })._retry = true;
      if (!refreshing) {
        refreshing = (async () => {
          const s = opts.storage.get();
          if (!s?.refreshToken) throw error;
          // user : rôle relu en base par le gateway ; absent (ancien gateway), le profil stocké est conservé
          const res = await axios.post<Pick<AuthSession, 'accessToken' | 'refreshToken' | 'expiresIn'> & Partial<Pick<AuthSession, 'user'>>>(
            `${opts.baseUrl}/api/v1/auth/refresh`,
            { refreshToken: s.refreshToken },
          );
          const next: AuthSession = { ...s, ...res.data, user: res.data.user ?? s.user };
          opts.storage.set(next);
          opts.onSessionRefreshed?.(next);
          return next.accessToken;
        })()
          .catch((e) => {
            opts.storage.clear();
            opts.onUnauthorized?.();
            throw e;
          })
          .finally(() => { refreshing = null; });
      }
      const newToken = await refreshing;
      original.headers!.Authorization = `Bearer ${newToken}`;
      return client(original);
    },
  );

  return client;
};

export type { AuthSession, SessionStorage };
